import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";

import { API_URL } from "../../../apiConfig";
import { checkAuthenticationAdmin } from "../../Authentication/checkAuthentication";
import CustomAlert from "../../UI/Alerts/CustomAlert";
import CustomTypography from "../../UI/Typography/CustomTypography";
import ItemTable from "../../UI/Tables/ItemTable";

const theme = createTheme({
  shape: {
    borderRadius: 5
  }
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#F9F9F9",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center"
}));

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  return new Date(value).toLocaleDateString("en-GB");
};

const BoardAdminElearningUsers = () => {
  const [_content, setContent] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [generatedUser, setGeneratedUser] = useState(null);
  const [formValues, setFormValues] = useState({ username: "", email: "" });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [quizCategories, setQuizCategories] = useState([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [quizFormValues, setQuizFormValues] = useState({
    id: null,
    questionKey: "",
    categoryKey: "",
    prompt: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOption: 0,
    displayOrder: 0
  });
  const currentUser = useSelector((state) => state.auth.user);

  const getAuthHeaders = () => {
    const userString = localStorage.getItem("user");
    const jwtToken = userString ? JSON.parse(userString).token : null;
    return jwtToken ? { "x-access-token": jwtToken } : {};
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "username", headerName: "Username", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "passedCourseCount",
      headerName: "Progress",
      width: 110,
      renderCell: (params) => {
        const progress = params?.row?.materialProgress;
        if (progress) {
          return progress;
        }

        const passed = Number(params?.row?.passedCourseCount);
        const total = Number(params?.row?.totalCourses);

        if (Number.isFinite(passed) && Number.isFinite(total) && total > 0) {
          return `${passed}/${total}`;
        }

        return "0/7";
      }
    },
    {
      field: "isCompleted",
      headerName: "Completed",
      width: 130,
      renderCell: (params) => (params?.value ? "Yes" : "No")
    },
    {
      field: "isSuccessful",
      headerName: "Successful",
      width: 130,
      renderCell: (params) => (params?.value ? "Yes" : "No")
    },
    {
      field: "finalQuizScore",
      headerName: "Quiz Score",
      width: 120,
      renderCell: (params) => {
        const parsed = Number(params?.row?.finalQuizScore);
        if (Number.isFinite(parsed)) {
          return `${parsed}%`;
        }

        if (params?.row?.isSuccessful) {
          return "Passed";
        }

        return "-";
      }
    },
    {
      field: "lastCompletionAt",
      headerName: "Last Completion",
      width: 210,
      renderCell: (params) => {
        const dateValue = params?.row?.lastCompletionAt;
        return dateValue ? formatDate(dateValue) : "-";
      }
    }
  ];

  const resetQuizForm = () => {
    setQuizFormValues({
      id: null,
      questionKey: "",
      categoryKey: quizCategories[0]?.key || "",
      prompt: "",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      correctOption: 0,
      displayOrder: 0
    });
  };

  const handleOpenAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleRowSelection = (selectedIds) => {
    setSelectedRowIds(selectedIds);
  };

  const fetchUsers = () => {
    const headers = getAuthHeaders();
    axios
      .get(`${API_URL}/elearning/admin/users`, { headers })
      .then((response) => {
        const data = response.data?.users || [];
        setRows(data);
      })
      .catch((error) => {
        console.error("Error fetching e-learning users:", error);
        handleOpenAlert("error", "Failed to fetch e-learning users.");
      });
  };

  const fetchQuizData = () => {
    const headers = getAuthHeaders();

    axios
      .get(`${API_URL}/elearning/admin/quiz`, { headers })
      .then((response) => {
        const categories = response.data?.categories || [];
        const questions = response.data?.questions || [];

        setQuizCategories(categories);
        setQuizQuestions(questions);

        if (!quizFormValues.categoryKey && categories.length > 0) {
          setQuizFormValues((prev) => ({
            ...prev,
            categoryKey: categories[0].key
          }));
        }
      })
      .catch((error) => {
        console.error("Error fetching quiz questions:", error);
        handleOpenAlert("error", error.response?.data?.message || "Failed to fetch quiz questions.");
      });
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        try {
          const content = await checkAuthenticationAdmin();
          setContent(content);
        } catch (error) {
          const _content = error.message || "Error checking authentication.";
          setContent(_content);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    checkAuthenticationAdmin();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
      fetchQuizData();
    }
  }, [currentUser]);

  const openCreateDialog = () => {
    setFormValues({ username: "", email: "" });
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setGeneratedPassword("");
    setGeneratedUser(null);
  };

  const handleCreateUser = () => {
    if (!formValues.username || !formValues.email) {
      handleOpenAlert("error", "Username and email are required.");
      return;
    }

    const headers = getAuthHeaders();

    axios
      .post(`${API_URL}/elearning/admin/users`, formValues, { headers })
      .then((response) => {
        setGeneratedPassword(response.data.password);
        setGeneratedUser(response.data.user);
        setPasswordDialogOpen(true);
        setCreateDialogOpen(false);
        fetchUsers();
      })
      .catch((error) => {
        console.error("Error creating e-learning user:", error);
        const errorMsg = error.response?.data?.message || "Failed to create e-learning user.";
        handleOpenAlert("error", errorMsg);
      });
  };

  const handleResetPassword = () => {
    if (selectedRowIds.length !== 1) {
      handleOpenAlert("error", "Select exactly one user to reset password.");
      return;
    }

    const userId = selectedRowIds[0];
    const headers = getAuthHeaders();

    axios
      .post(`${API_URL}/elearning/admin/users/${userId}/reset-password`, null, { headers })
      .then((response) => {
        const newPassword = response.data.password;
        const user = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email
        };
        setGeneratedPassword(newPassword);
        setGeneratedUser(user);
        setPasswordDialogOpen(true);
        fetchUsers();
      })
      .catch((error) => {
        console.error("Error resetting password:", error);
        handleOpenAlert("error", error.response?.data?.message || "Failed to reset password.");
      });
  };

  const handleDeleteUsers = () => {
    if (selectedRowIds.length === 0) {
      handleOpenAlert("error", "Select at least one user to delete.");
      return;
    }

    if (!window.confirm(`Delete ${selectedRowIds.length} user(s)? This cannot be undone.`)) {
      return;
    }

    const headers = getAuthHeaders();

    axios
      .post(`${API_URL}/elearning/admin/users/delete`, { ids: selectedRowIds }, { headers })
      .then(() => {
        handleOpenAlert("success", "User(s) deleted successfully.");
        setSelectedRowIds([]);
        fetchUsers();
      })
      .catch((error) => {
        console.error("Error deleting users:", error);
        handleOpenAlert("error", error.response?.data?.message || "Failed to delete user(s).");
      });
  };

  const handleEditQuestion = (question) => {
    const options = Array.isArray(question.options) ? question.options : [];
    setQuizFormValues({
      id: question.id,
      questionKey: question.questionKey || "",
      categoryKey: question.categoryKey || quizCategories[0]?.key || "",
      prompt: question.prompt || "",
      optionA: options[0] || "",
      optionB: options[1] || "",
      optionC: options[2] || "",
      optionD: options[3] || "",
      correctOption: Number.isInteger(question.correctOption) ? question.correctOption : 0,
      displayOrder: Number.isInteger(question.displayOrder) ? question.displayOrder : 0
    });
  };

  const handleDeleteQuestion = (questionId) => {
    if (!window.confirm("Delete this quiz question?")) {
      return;
    }

    const headers = getAuthHeaders();
    axios
      .delete(`${API_URL}/elearning/admin/quiz/questions/${questionId}`, { headers })
      .then(() => {
        handleOpenAlert("success", "Question deleted successfully.");
        fetchQuizData();
        if (quizFormValues.id === questionId) {
          resetQuizForm();
        }
      })
      .catch((error) => {
        console.error("Error deleting quiz question:", error);
        handleOpenAlert("error", error.response?.data?.message || "Failed to delete quiz question.");
      });
  };

  const handleSaveQuestion = () => {
    const options = [
      quizFormValues.optionA,
      quizFormValues.optionB,
      quizFormValues.optionC,
      quizFormValues.optionD
    ].map((option) => option.trim()).filter((option) => option.length > 0);

    if (!quizFormValues.categoryKey || !quizFormValues.prompt.trim() || options.length < 2) {
      handleOpenAlert("error", "Category, prompt and at least two options are required.");
      return;
    }

    const payload = {
      questionKey: quizFormValues.questionKey.trim(),
      categoryKey: quizFormValues.categoryKey,
      prompt: quizFormValues.prompt.trim(),
      options,
      correctOption: Number(quizFormValues.correctOption),
      displayOrder: Number(quizFormValues.displayOrder)
    };

    if (payload.correctOption < 0 || payload.correctOption >= options.length) {
      handleOpenAlert("error", "Correct option index must match one of the provided options.");
      return;
    }

    const headers = getAuthHeaders();
    setIsSavingQuestion(true);

    const request = quizFormValues.id
      ? axios.put(`${API_URL}/elearning/admin/quiz/questions/${quizFormValues.id}`, payload, { headers })
      : axios.post(`${API_URL}/elearning/admin/quiz/questions`, payload, { headers });

    request
      .then(() => {
        handleOpenAlert("success", quizFormValues.id ? "Question updated successfully." : "Question created successfully.");
        fetchQuizData();
        resetQuizForm();
      })
      .catch((error) => {
        console.error("Error saving quiz question:", error);
        handleOpenAlert("error", error.response?.data?.message || "Failed to save quiz question.");
      })
      .finally(() => {
        setIsSavingQuestion(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl">
        <Box sx={{ flexGrow: 1, bgcolor: "white" }}>
          <Grid container spacing={2} style={{ paddingTop: "50px" }}>
            <Grid item xs={12}>
              <Item
                style={{
                  paddingTop: "30px",
                  paddingBottom: "30px",
                  backgroundImage: 'url("/images/BannerImages/2.jpg")',
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              >
                <CustomTypography variant="h3" text="E-Learning Users" color="white" />
              </Item>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ paddingTop: "50px", display: "flex", flexDirection: "column" }}>
            <Grid item xs={12}>
              <Item style={{ paddingTop: "30px", paddingBottom: "30px", display: "flex", justifyContent: "space-between" }}>
                <CustomTypography variant="h4" text="User List" color="Teal" />
                <div style={{ display: "flex", gap: "12px" }}>
                  <Button variant="contained" color="primary" onClick={openCreateDialog}>
                    Create User
                  </Button>
                  <Button variant="outlined" color="warning" onClick={handleResetPassword}>
                    Reset Password
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleDeleteUsers}>
                    Delete User
                  </Button>
                </div>
              </Item>
            </Grid>

            <Grid item xs={12}>
              <ItemTable
                data={rows}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 50]}
                onRowSelectionModelChange={handleRowSelection}
              />
            </Grid>

            <Grid item xs={12}>
              <Item style={{ paddingTop: "30px", paddingBottom: "30px", alignItems: "stretch" }}>
                <CustomTypography variant="h4" text="Quiz Question Management" color="Teal" />

                <Box sx={{ mt: 3, width: "100%" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Question Key"
                        value={quizFormValues.questionKey}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, questionKey: event.target.value }))}
                        helperText="Leave empty to auto-generate"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        select
                        label="Category"
                        value={quizFormValues.categoryKey}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, categoryKey: event.target.value }))}
                      >
                        {quizCategories.map((category) => (
                          <MenuItem key={category.key} value={category.key}>
                            {category.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Display Order"
                        value={quizFormValues.displayOrder}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, displayOrder: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Question Prompt"
                        value={quizFormValues.prompt}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, prompt: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Option A"
                        value={quizFormValues.optionA}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, optionA: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Option B"
                        value={quizFormValues.optionB}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, optionB: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Option C"
                        value={quizFormValues.optionC}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, optionC: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Option D"
                        value={quizFormValues.optionD}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, optionD: event.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Correct Option Index"
                        value={quizFormValues.correctOption}
                        onChange={(event) => setQuizFormValues((prev) => ({ ...prev, correctOption: event.target.value }))}
                        helperText="0 for Option A, 1 for Option B, etc"
                      />
                    </Grid>
                    <Grid item xs={12} md={8} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Button variant="contained" onClick={handleSaveQuestion} disabled={isSavingQuestion}>
                        {quizFormValues.id ? "Update Question" : "Add Question"}
                      </Button>
                      <Button variant="outlined" onClick={resetQuizForm}>
                        Clear Form
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 3, width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {quizQuestions.map((question) => (
                    <Paper key={question.id} variant="outlined" sx={{ p: 2, textAlign: "left" }}>
                      <CustomTypography
                        variant="body1"
                        text={`${question.categoryTitle || "Uncategorized"} • ${question.questionKey}`}
                        color="black"
                      />
                      <CustomTypography variant="body2" text={question.prompt} color="black" />
                      <CustomTypography
                        variant="body2"
                        text={`Correct option: ${question.correctOption + 1} | Options: ${(question.options || []).join(" | ")}`}
                        color="black"
                      />
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => handleEditQuestion(question)}>
                          Edit
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteQuestion(question.id)}>
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Item>
            </Grid>
          </Grid>
        </Box>

        <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create E-Learning User</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              margin="normal"
              value={formValues.username}
              onChange={(event) => setFormValues((prev) => ({ ...prev, username: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Email"
              margin="normal"
              value={formValues.email}
              onChange={(event) => setFormValues((prev) => ({ ...prev, email: event.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateUser}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Temporary Password</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <CustomTypography
                variant="body1"
                text={generatedUser ? `User: ${generatedUser.username} (${generatedUser.email})` : ""}
                color="black"
              />
            </Box>
            <TextField fullWidth label="Password" value={generatedPassword} InputProps={{ readOnly: true }} />
            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => {
                navigator.clipboard.writeText(generatedPassword);
                handleOpenAlert("success", "Password copied to clipboard!");
              }}
            >
              Copy Password
            </Button>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={closePasswordDialog}>
              Done
            </Button>
          </DialogActions>
        </Dialog>

        <CustomAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Container>
    </ThemeProvider>
  );
};

export default BoardAdminElearningUsers;
